import { MapPin, Phone, Mail, Facebook, Instagram, MessageCircle } from 'lucide-react'; // Added Instagram and MessageCircle import
import { Link } from 'react-router-dom';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Destinations', path: '/destinations' },
    { name: 'Activities', path: '/activities' },
    { name: 'Accommodation', path: '/accommodation' },
    { name: 'Transfers', path: '/transfers' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  const destinations = [
    'Victoria Falls',
    'South Luangwa',
    'Chobe National Park',
    'Kafue National Park',
    'Maasai Mara',
    'Mana Pools'
  ];

  return (
    <footer className="bg-foreground text-background">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img src="/images/logo.png" alt="Peregrine Tours Logo" className="w-16 h-16 object-contain opacity-90 drop-shadow-lg bg-white/10 rounded-lg p-1" />
              <div className="flex flex-col">
                <span className="font-serif font-bold text-lg leading-tight">
                  Peregrine Tours
                </span>
                <span className="text-xs text-background/70 tracking-wider">
                  & SAFARIS
                </span>
              </div>
            </div>
            <p className="text-sm text-background/70 leading-relaxed mb-6">
              Creating unforgettable journeys across Southern and Eastern Africa since 2020. Your trusted partner for authentic safari experiences.
            </p>
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=61560052620034"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-background/10 hover:bg-primary flex items-center justify-center transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              {/* Instagram Added Here */}
              <a
                href="https://instagram.com/peregrine_tours"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-background/10 hover:bg-primary flex items-center justify-center transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-sm text-background/70 hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Destinations */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Destinations</h3>
            <ul className="space-y-3">
              {destinations.map((destination, index) => (
                <li key={index}>
                  <Link
                    to="/destinations"
                    className="text-sm text-background/70 hover:text-primary transition-colors duration-200"
                  >
                    {destination}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-background/70">
                  Plot# 2613/1747 Pula Street, Off Botswana Road, Livingstone, Zambia
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <a
                  href="tel:+260961187200"
                  className="text-sm text-background/70 hover:text-primary transition-colors"
                >
                  +260 961 187 200
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-5 h-5 text-primary flex-shrink-0 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <a
                  href="https://wa.me/260961187200"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-background/70 hover:text-primary transition-colors"
                >
                  WhatsApp Us
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:info@peregrinetoursandsafaris.com"
                  className="text-sm text-background/70 hover:text-primary transition-colors break-all"
                >
                  info@peregrinetoursandsafaris.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-background/70 text-center md:text-left">
              © {currentYear} Peregrine Tours & Safaris. All rights reserved. | Web dev by Themaplin Inc Production.
            </p>
            <div className="flex items-center space-x-6">
              <button className="text-sm text-background/70 hover:text-primary transition-colors">
                Privacy Policy
              </button>
              <button className="text-sm text-background/70 hover:text-primary transition-colors">
                Terms of Service
              </button>
              <Link to="/refund-policy" className="text-sm text-background/70 hover:text-primary transition-colors">
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};